import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, AlertTriangle, RotateCcw, ShieldAlert } from "lucide-react";

export const QTYPE_LABELS = {
  multiple_choice: "Multiple choice", true_false: "True / False", scenario: "Scenario judgement",
  hazard: "Hazard recognition", multiple_select: "Select all that apply", numerical: "Numerical",
};

function fmtAns(a) {
  if (Array.isArray(a)) return a.join(", ");
  if (a === undefined || a === null || a === "") return "—";
  return String(a);
}

export function QuizResult({ result, onRetry, testidPrefix = "quiz" }) {
  const passed = result.passed ?? result.status === "pass";
  const mgmtReview = result.status === "management_review";
  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-result`}>
      <div className={`rounded-md border p-5 text-center ${
        mgmtReview ? "bg-amber-50 border-amber-300" : passed ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"
      }`}>
        <p className="font-heading font-black text-4xl text-charcoal" data-testid={`${testidPrefix}-score`}>{result.score_pct}%</p>
        <p className={`font-heading font-bold uppercase tracking-wide mt-1 ${
          mgmtReview ? "text-amber-700" : passed ? "text-emerald-700" : "text-red-700"
        }`} data-testid={`${testidPrefix}-outcome`}>
          {mgmtReview ? "Management review required" : passed ? `Passed (mark ${result.pass_mark}%)` : `Not yet — pass mark ${result.pass_mark}%`}
        </p>
        {result.sc_wrong > 0 && (
          <p className="text-xs text-red-600 mt-2 flex items-center justify-center gap-1" data-testid={`${testidPrefix}-sc-note`}>
            <ShieldAlert className="h-3.5 w-3.5" /> {result.sc_wrong} safety-critical question{result.sc_wrong > 1 ? "s" : ""} answered incorrectly
          </p>
        )}
        {onRetry && !passed && (
          <Button onClick={onRetry} variant="outline" className="mt-4 border-steel text-charcoal bg-white" data-testid={`${testidPrefix}-retry-btn`}>
            <RotateCcw className="h-4 w-4 mr-1.5" /> Revisit the learning, then retry
          </Button>
        )}
      </div>

      <div className="space-y-3" data-testid={`${testidPrefix}-review`}>
        {(result.responses || []).map((r, i) => (
          <div key={r.question_id || i} className={`rounded-md border p-4 ${r.correct ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`}
            data-testid={`${testidPrefix}-review-${i}`}>
            <div className="flex items-start gap-2.5">
              {r.correct ? <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <p className="text-sm text-charcoal font-medium">{r.question}</p>
                {r.safety_critical && (
                  <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-widest text-red-600">
                    <ShieldAlert className="h-3 w-3" /> Safety-critical
                  </span>
                )}
                <p className="text-xs mt-1.5 text-slate-600">Your answer: <span className={r.correct ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{fmtAns(r.given)}</span></p>
                {!r.correct && <p className="text-xs mt-0.5 text-slate-600">Correct answer: <span className="text-emerald-700 font-semibold">{fmtAns(r.correct_answer)}</span></p>}
                {r.explanation && <p className="text-xs text-slate-500 mt-1.5 leading-relaxed border-l-2 border-lomond pl-2.5">{r.explanation}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function QuizRunner({ questions, passMark, onSubmit, submitting, onRetry, testidPrefix = "quiz" }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const isAnswered = (q) => {
    const a = answers[q.id];
    if (Array.isArray(a)) return a.length > 0;
    return a !== undefined && String(a).trim() !== "";
  };
  const answeredCount = questions.filter(isAnswered).length;

  const setSingle = (q, v) => setAnswers({ ...answers, [q.id]: v });
  const toggleMulti = (q, opt) => {
    const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
    setAnswers({ ...answers, [q.id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] });
  };

  const submit = async () => {
    const r = await onSubmit(answers);
    if (r) setResult(r);
  };

  if (result) {
    return <QuizResult result={result} testidPrefix={testidPrefix}
      onRetry={() => { setResult(null); setAnswers({}); onRetry?.(); }} />;
  }

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-runner`}>
      <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">
        {questions.length} questions — pass mark {passMark}% — {answeredCount}/{questions.length} answered
      </p>
      {questions.map((q, i) => (
        <div key={q.id} className="bg-white border border-steel rounded-md p-4 sm:p-5" data-testid={`${testidPrefix}-q-${i}`}>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">
            Q{i + 1} — {QTYPE_LABELS[q.qtype] || q.qtype}
          </p>
          <p className="text-sm sm:text-base text-charcoal font-medium mb-3">{q.question}</p>
          {q.qtype === "numerical" ? (
            <Input value={answers[q.id] || ""} onChange={(e) => setSingle(q, e.target.value)}
              className="bg-white border-steel text-charcoal h-12 text-base max-w-xs" placeholder="Your answer"
              data-testid={`${testidPrefix}-q-${i}-input`} />
          ) : q.qtype === "multiple_select" ? (
            <div className="space-y-2">
              {(q.options || []).map((opt) => {
                const sel = (answers[q.id] || []).includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggleMulti(q, opt)}
                    className={`w-full text-left px-4 py-3.5 rounded-md border text-sm transition-colors ${
                      sel ? "bg-lomond/20 border-lomond text-charcoal font-semibold" : "bg-white border-steel text-slate-600 hover:border-lomond"
                    }`} data-testid={`${testidPrefix}-q-${i}-opt`}>
                    <span className={`inline-block h-4 w-4 mr-2.5 rounded-sm border align-middle ${sel ? "bg-lomond border-lomond" : "border-steel bg-white"}`} />
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {(q.qtype === "true_false" ? ["True", "False"] : q.options || []).map((opt) => (
                <button key={opt} type="button" onClick={() => setSingle(q, opt)}
                  className={`w-full text-left px-4 py-3.5 rounded-md border text-sm transition-colors ${
                    answers[q.id] === opt ? "bg-lomond text-black border-lomond font-semibold" : "bg-white border-steel text-slate-600 hover:border-lomond"
                  }`} data-testid={`${testidPrefix}-q-${i}-opt`}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      <Button onClick={submit} disabled={submitting || answeredCount < questions.length}
        className="w-full bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12 text-base"
        data-testid={`${testidPrefix}-submit-btn`}>
        {submitting ? "Submitting..." : answeredCount < questions.length ? `Answer all questions (${answeredCount}/${questions.length})` : "Submit Answers"}
      </Button>
    </div>
  );
}
